import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type LibraryCategory, type LibrarySource, type Tag } from "../api/client";
import AttributionBadge from "../components/Attribution";

type LibraryTab = "all" | "private";

export default function LibraryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<LibrarySource[]>([]);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<LibraryTab>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const nextItems = await api.listLibrary({ category: category || undefined, q: q || undefined });
      setItems(nextItems);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, [category]);

  useEffect(() => {
    Promise.all([api.listLibraryCategories(true), api.listTags()])
      .then(([nextCategories, nextTags]) => {
        setCategories(nextCategories);
        setTags(nextTags);
      })
      .catch((e) => setError(String(e)));
  }, []);

  async function handleAddTag(source: LibrarySource, tagName: string) {
    const name = tagName.trim();
    if (!name) return;
    try {
      let tag = tags.find((t) => t.name.toLowerCase() === name.toLowerCase());
      if (!tag) {
        tag = await api.createTag(name);
        setTags((current) => (current.some((t) => t.id === tag!.id) ? current : [...current, tag!]));
      }
      await api.addItemTag("library", source.id, tag.id);
      setItems((current) =>
        current.map((item) => {
          if (item.id !== source.id) return item;
          if (item.tags.some((t) => t.id === tag!.id)) return item;
          return { ...item, tags: [...item.tags, { id: tag!.id, name: tag!.name, color: tag!.color }] };
        }),
      );
    } catch (e) {
      alert(`Couldn't add tag: ${(e as Error).message}`);
    }
  }

  async function handleRemoveTag(source: LibrarySource, tagId: number) {
    try {
      await api.removeItemTag("library", source.id, tagId);
      setItems((current) =>
        current.map((item) =>
          item.id === source.id ? { ...item, tags: item.tags.filter((tag) => tag.id !== tagId) } : item,
        ),
      );
    } catch (e) {
      alert(`Couldn't remove tag: ${(e as Error).message}`);
    }
  }

  const visibleItems = useMemo(() => {
    if (tab === "private") {
      return items.filter((source) => Boolean(source.abby_private) || source.source_category === "abby_private");
    }
    return items;
  }, [items, tab]);

  const privateCount = useMemo(
    () => items.filter((source) => Boolean(source.abby_private) || source.source_category === "abby_private").length,
    [items],
  );

  return (
    <>
      <div className="ar-header">
        <h2>Library</h2>
        <span className="crumb">/ live sources</span>
        <span className="spacer" />
        <button className="ar-btn" type="button" onClick={() => { setShowUpload(true); setShowUrl(false); }}>
          Upload
        </button>
        <button className="ar-btn" type="button" onClick={() => { setShowUrl(true); setShowUpload(false); }}>
          Add by URL
        </button>
      </div>

      <div className="ar-body">
        <div className="ar-toolbar">
          <select className="ar-select" value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">All categories</option>
            {categories.filter((c) => !c.archived).map((c) => (
              <option key={c.id} value={c.key}>{c.label}</option>
            ))}
            {categories.some((c) => c.archived) && <option disabled>Archived categories</option>}
            {categories.filter((c) => c.archived).map((c) => (
              <option key={c.id} value={c.key}>{c.label} (archived)</option>
            ))}
          </select>
          <input
            className="ar-input"
            placeholder="Search title or authors..."
            value={q}
            onChange={(event) => setQ(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") refresh();
            }}
          />
          <button className="ar-btn" type="button" onClick={refresh}>Search</button>
        </div>

        <div className="ar-tabs" style={{ marginBottom: 18 }}>
          <button className={tab === "all" ? "active" : ""} type="button" onClick={() => setTab("all")}>
            all sources <span className="count">{items.length}</span>
          </button>
          <button className={tab === "private" ? "active" : ""} type="button" onClick={() => setTab("private")}>
            private <span className="count">{privateCount}</span>
          </button>
        </div>

        {showUpload && <UploadCard categories={categories} onClose={() => setShowUpload(false)} onDone={refresh} />}
        {showUrl && <UrlAddCard categories={categories} onClose={() => setShowUrl(false)} onDone={refresh} />}

        {loading && <p className="ar-muted"><span className="ar-spinner" /> Loading library...</p>}
        {error && <p className="error">{error}</p>}

        {!loading && (
          <div className="ar-lib">
            {visibleItems.map((source) => (
              <div
                key={source.id}
                className="ar-libitem"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/library/${source.id}`)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") navigate(`/library/${source.id}`);
                }}
              >
                <div>
                  <div className="title">{source.title}</div>
                  <div className="meta">
                    {source.abby_private ? (
                      <span className="badge private">Private</span>
                    ) : (
                      <span className="badge">{categoryLabel(categories, source.source_category)}</span>
                    )}
                    {source.authors && <span>{source.authors}</span>}
                    {source.year && <span>{source.year}</span>}
                    <AttributionBadge createdBy={source.created_by} compact />
                    <span>{source.chunk_count} chunks</span>
                    <span>{formatBytes(source.byte_size)}</span>
                    <span>Added {formatDate(source.added_at)}</span>
                    {source.status !== "ready" && <span className="error">{source.status}</span>}
                  </div>
                  <LibraryTagBar
                    source={source}
                    tags={tags}
                    onAddTag={(name) => handleAddTag(source, name)}
                    onRemoveTag={(tagId) => handleRemoveTag(source, tagId)}
                  />
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    className="ar-btn ghost sm"
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/library/${source.id}`);
                    }}
                  >
                    Open
                  </button>
                  <button
                    className="ar-btn ghost sm danger"
                    type="button"
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (!confirm(`Delete "${source.title}"? This removes its chunks from retrieval.`)) return;
                      await api.deleteLibrarySource(source.id);
                      refresh();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            {visibleItems.length === 0 && (
              <div className="ar-empty" style={{ minHeight: 260 }}>
                <p className="t">No library sources found.</p>
                <p className="h">Upload a document or add a URL to populate the live library.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function LibraryTagBar({
  source,
  tags,
  onAddTag,
  onRemoveTag,
}: {
  source: LibrarySource;
  tags: Tag[];
  onAddTag: (name: string) => void;
  onRemoveTag: (tagId: number) => void;
}) {
  const [tagInput, setTagInput] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const sourceTags = source.tags ?? [];
  const sourceTagIds = useMemo(() => new Set(sourceTags.map((tag) => tag.id)), [sourceTags]);
  const availableTags = useMemo(
    () => tags.filter((tag) => !sourceTagIds.has(tag.id)),
    [tags, sourceTagIds],
  );

  function commitTag(name: string) {
    const cleanName = name.trim();
    if (!cleanName) return;
    onAddTag(cleanName);
    setTagInput("");
    setPickerOpen(false);
  }

  return (
    <div
      className="tag-edit-row library-tag-row"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <span className="muted" style={{ fontSize: 12 }}>Tags:</span>
      {sourceTags.map((tag) => (
        <span
          key={tag.id}
          className="tag-chip removable"
          style={tag.color ? { borderColor: tag.color } : undefined}
        >
          <span className="tag-dot" style={{ background: tag.color || "var(--accent)" }} />
          {tag.name}
          <button
            type="button"
            className="tag-remove"
            aria-label={`Remove tag ${tag.name}`}
            onClick={() => onRemoveTag(tag.id)}
          >
            x
          </button>
        </span>
      ))}
      {pickerOpen ? (
        <div className="library-tag-picker">
          <div className="library-tag-picker-head">
            <span>Existing tags</span>
            <button type="button" className="ghost-link" onClick={() => setPickerOpen(false)}>
              Done
            </button>
          </div>
          <div className="library-tag-options">
            {availableTags.length > 0 ? (
              availableTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  className="library-tag-option"
                  style={tag.color ? { borderColor: tag.color } : undefined}
                  onClick={() => commitTag(tag.name)}
                >
                  <span className="tag-dot" style={{ background: tag.color || "var(--accent)" }} />
                  {tag.name}
                </button>
              ))
            ) : (
              <span className="library-tag-empty">No unused tags</span>
            )}
          </div>
          <label className="library-tag-new">
            <span>New tag</span>
            <span className="library-tag-new-row">
              <input
                autoFocus
                className="tag-input"
                value={tagInput}
                onChange={(event) => setTagInput(event.target.value)}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitTag(tagInput);
                  } else if (event.key === "Escape") {
                    setTagInput("");
                    setPickerOpen(false);
                  }
                }}
                placeholder="tag name..."
              />
              <button type="button" className="ghost-link" disabled={!tagInput.trim()} onClick={() => commitTag(tagInput)}>
                Add
              </button>
            </span>
          </label>
        </div>
      ) : (
        <button type="button" className="ghost-link" onClick={() => setPickerOpen(true)}>
          + tag
        </button>
      )}
    </div>
  );
}

function categoryLabel(categories: LibraryCategory[], key: string): string {
  return categories.find((category) => category.key === key)?.label ?? key;
}

function formatDate(value: number): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size >= 10 || unit === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unit]}`;
}

function UploadCard({
  categories,
  onClose,
  onDone,
}: {
  categories: LibraryCategory[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [authors, setAuthors] = useState("");
  const [year, setYear] = useState("");
  const [category, setCategory] = useState("abby_private");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await api.uploadLibrary(file, {
        title: title || undefined,
        authors: authors || undefined,
        year: year ? Number(year) : undefined,
        source_category: category,
        abby_private: category === "abby_private",
      });
      await onDone();
      onClose();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ar-section" style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ margin: 0 }}>Upload a document</h3>
        <button className="ar-btn ghost sm" type="button" onClick={onClose}>Cancel</button>
      </div>
      <div className="ar-form" style={{ maxWidth: 760 }}>
        <div className="field">
          <label>File</label>
          <input type="file" accept=".pdf,.docx,.txt,.md,.html" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
        </div>
        <div className="field">
          <label>Title <span className="opt">(optional)</span></label>
          <input className="ar-input" value={title} onChange={(event) => setTitle(event.target.value)} />
        </div>
        <div className="field">
          <label>Authors <span className="opt">(optional)</span></label>
          <input className="ar-input" value={authors} onChange={(event) => setAuthors(event.target.value)} />
        </div>
        <div className="field">
          <label>Year <span className="opt">(optional)</span></label>
          <input className="ar-input" value={year} onChange={(event) => setYear(event.target.value)} />
        </div>
        <div className="field">
          <label>Category</label>
          <select className="ar-select" value={category} onChange={(event) => setCategory(event.target.value)}>
            {categories.filter((c) => !c.archived).map((c) => (
              <option key={c.id} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="ar-btn primary" type="button" onClick={submit} disabled={!file || busy}>
            {busy ? <><span className="ar-spinner" /> Ingesting...</> : "Upload and ingest"}
          </button>
          {error && <span className="error">{error}</span>}
        </div>
      </div>
    </div>
  );
}

function UrlAddCard({
  categories,
  onClose,
  onDone,
}: {
  categories: LibraryCategory[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("methods_general");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!url.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.addLibraryByUrl({
        url: url.trim(),
        title: title || undefined,
        source_category: category,
        abby_private: false,
      });
      await onDone();
      onClose();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ar-section" style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ margin: 0 }}>Add by URL</h3>
        <button className="ar-btn ghost sm" type="button" onClick={onClose}>Cancel</button>
      </div>
      <div className="ar-form" style={{ maxWidth: 760 }}>
        <div className="field">
          <label>URL</label>
          <input className="ar-input" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://..." />
        </div>
        <div className="field">
          <label>Title <span className="opt">(optional)</span></label>
          <input className="ar-input" value={title} onChange={(event) => setTitle(event.target.value)} />
        </div>
        <div className="field">
          <label>Category</label>
          <select className="ar-select" value={category} onChange={(event) => setCategory(event.target.value)}>
            {categories.filter((c) => !c.archived).map((c) => (
              <option key={c.id} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="ar-btn primary" type="button" onClick={submit} disabled={!url.trim() || busy}>
            {busy ? <><span className="ar-spinner" /> Fetching...</> : "Fetch and ingest"}
          </button>
          {error && <span className="error">{error}</span>}
        </div>
      </div>
    </div>
  );
}
